import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getCategories } from "../services/categoriesApi";
import { translateToEnglish } from "./translations";
import { useAuth } from "./AuthContext";

const CategoriesContext = createContext();

export const CategoriesProvider = ({ children }) => {
    const [serviceCategories, setServiceCategories] = useState([]);
    const [productsCategories, setProductsCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    // Si el prefetch inicial falla (p.ej. token vencido al cargar la app), nos
    // reintentamos cuando AuthContext reporta un cambio de sesión.
    const { user } = useAuth();

    const fetchAll = useCallback(async () => {
        setLoadingCategories(true);
        try {
            const [services, products] = await Promise.all([
                getCategories("service"),
                getCategories("products"),
            ]);
            setServiceCategories(services || []);
            setProductsCategories(products || []);
        } catch (error) {
            console.error("Error prefetching categories:", error);
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    // Fetch inicial + reintento cuando cambia la sesión (login tardío, refresh
    // de token, etc.) por si el primer intento se hizo sin auth.
    useEffect(() => {
        fetchAll();
    }, [fetchAll, user]);

    const getCategoriesByType = useCallback((type) => {
        if (!type) return [];
        const normalized = translateToEnglish(type);
        if (normalized === "service") return serviceCategories;
        if (normalized === "products") return productsCategories;
        return [];
    }, [serviceCategories, productsCategories]);

    const allCategories = useMemo(
        () => [...serviceCategories, ...productsCategories],
        [serviceCategories, productsCategories]
    );

    const value = {
        serviceCategories,
        productsCategories,
        allCategories,
        loadingCategories,
        getCategoriesByType,
        refreshCategories: fetchAll,
    };

    return (
        <CategoriesContext.Provider value={value}>
            {children}
        </CategoriesContext.Provider>
    );
};

export const useCategories = () => useContext(CategoriesContext);
